import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CLINICAL_FOCUS_TRACKS, LICENSE_TYPES } from '../lib/clinicianTracks';
import {
  ArrowLeft, ArrowRight, Mail, Lock, User, Phone,
  ShieldCheck, Sparkles, BadgeCheck, Heart, Stethoscope, Building2, Hospital,
  X, Send, Loader2, Wand2, MessageSquare, KeyRound, CheckCircle2, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { useModalClose, CLOSE_BTN_STYLE } from '../hooks/useModalClose';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { TrustBadge } from '../components/brand/TrustBadge';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import { isSurfaceLive } from '../lib/caraloom_v2_ia';
import { PasswordInput } from '../components/brand/PasswordInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = 'https://images.unsplash.com/photo-1770117663190-046672d8eaa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtYWNybyUyMG1lZGljYWwlMjBpbnN0cnVtZW50JTIwcHJlY2lzaW9ufGVufDB8fHx8MTc4MTA2OTYyNHww&ixlib=rb-4.1.0&q=85';
const HELP_MD_COMPONENTS = {
  p: ({ children }) => <p className="text-sm mb-2 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-cn-primary">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
};

export default function AuthPage() {
  const roleParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('role') : null;
  const nextParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('next') : null;
  const emailParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('email') : null;
  const VALID_SIGNUP_ROLES = ['patient', 'clinician', 'agency', 'discharge_planner'];
  const presetRole = VALID_SIGNUP_ROLES.includes(roleParam) ? roleParam : null;
  const [isLogin, setIsLogin] = useState(!presetRole);
  const [loading, setLoading] = useState(false);
  const [showMagic, setShowMagic] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showSignInHelp, setShowSignInHelp] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [formData, setFormData] = useState({
    email: emailParam || '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: presetRole || 'patient',
    license_type: 'RN',
    npi: '',
    license_state: '',
    clinical_focus_areas: [],
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const toggleFocus = (id) => {
    setFormData((prev) => {
      const set = new Set(prev.clinical_focus_areas);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, clinical_focus_areas: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result?.mfaRequired) {
          setMfaChallenge({ mfaToken: result.mfaToken, email: result.email });
          return;
        }
        // Iter 155 · MFA enforcement — role requires MFA but the user
        // hasn't enrolled yet. Backend already handed us a scoped
        // ``mfa_setup_only`` token via AuthContext.login → we just
        // need to route to the setup page.
        if (result?.mfaSetupRequired) {
          toast.info('Two-factor authentication is required for your role.');
          navigate('/mfa-setup');
          return;
        }
        toast.success('Welcome back to CaraLoom');
        navigateByRole(result.role);
      } else {
        const user = await register(formData);
        if (user?.mfaSetupRequired) {
          toast.info('Two-factor authentication is required for your role.');
          navigate('/mfa-setup');
          return;
        }
        toast.success('Your CaraLoom account is ready');
        navigateByRole(user.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const navigateByRole = (role) => {
    if (nextParam) {
      // Honor a post-auth redirect (e.g., accepting a care-circle invite),
      // but never land anyone on an archived surface — silently fall back
      // to the role's live dashboard (Phase 7 role alignment).
      try {
        const decoded = decodeURIComponent(nextParam);
        if (decoded.startsWith('/') && isSurfaceLive(decoded)) { navigate(decoded); return; }
      } catch (e) { /* fall through to role routing */ }
    }
    switch (role) {
      case 'patient': navigate('/patient/dashboard'); break;
      case 'clinician': navigate('/provider-os'); break;
      case 'agency': navigate('/provider-os'); break;
      case 'discharge_planner': navigate('/planner/dashboard'); break;
      case 'hospital_admin': navigate('/planner/dashboard'); break;
      case 'payer': navigate('/payer/dashboard'); break;
      case 'admin': navigate('/admin/dashboard'); break;
      default: navigate('/');
    }
  };

  const update = (k, v) => setFormData({ ...formData, [k]: v });
  const isClinicianSignup = !isLogin && formData.role === 'clinician';

  return (
    <div className="min-h-screen bg-cn-bg flex" data-testid="auth-page">
      {/* ============== Left visual panel ============== */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
        <img src={HERO_IMG} alt="CaraLoom" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-cn-primary/85 via-cn-primary/70 to-cn-sage-700/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Link to="/" className="inline-flex" data-testid="auth-home-link">
            <span className="inline-flex items-center gap-2.5">
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <path d="M9 8C5 8 3 12 5 16C7 20 11 22 16 22C21 22 25 20 27 16C29 12 27 8 23 8C19 8 17 12 16 16C15 20 13 24 9 24C5 24 3 20 5 16" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="16" cy="16" r="1.6" fill="#06B6D4" />
              </svg>
              <span className="font-display font-bold text-lg">CaraLoom</span>
            </span>
          </Link>

          <div className="space-y-8 max-w-md">
            <Sparkles className="w-7 h-7 text-cn-terracotta-300" />
            <h2 className="font-display text-4xl leading-tight font-semibold">
              The AI Operating System for Post-Acute Care.
            </h2>
            <p className="text-white/80 text-lg leading-relaxed">
              Referrals, workforce, operations, compliance, revenue cycle, and family
              coordination — one workflow layer across every post-acute organization.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold border border-white/15">
                <ShieldCheck className="w-3.5 h-3.5" /> HIPAA-ready
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold border border-white/15">
                <BadgeCheck className="w-3.5 h-3.5" /> Continuous credentialing
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold border border-white/15">
                <Sparkles className="w-3.5 h-3.5" /> AI-orchestrated
              </span>
            </div>
          </div>

          <p className="text-xs text-white/60 max-w-md">
            CaraLoom is a technology platform and does not directly provide care.
            Clinicians are independent licensed professionals.
          </p>
        </div>
      </div>

      {/* ============== Right form panel ============== */}
      <div className="flex-1 flex flex-col">
        <header className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-cn-border lg:border-b-0">
          <Link to="/" className="lg:hidden" data-testid="auth-mobile-home-link">
            <CaraLoomLogo />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-primary"
            data-testid="auth-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-6 md:px-10 py-10">
          <div className="w-full max-w-md">
            <div className="cn-eyebrow mb-3">{isLogin ? 'Welcome back' : (isClinicianSignup ? 'For independent clinicians' : 'Create your account')}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              {isLogin ? 'Sign in to CaraLoom' : (isClinicianSignup ? 'Launch your practice' : 'Join the post-acute network')}
            </h1>
            <p className="mt-3 text-cn-text-muted">
              {isLogin
                ? 'Continue coordinating care from your dashboard.'
                : (isClinicianSignup
                    ? 'Set your rates, choose your patients, and run your independent practice on one operating layer.'
                    : 'Pick your role — each one drops into a workspace built for it.')}
            </p>

            {/* Role toggle (only on signup) — Phase 7 role alignment: only the
                four roles with a job in the discharge loop are self-registerable.
                Payer is parked (existing payer accounts still log in). Backend
                role values unchanged; `agency` displays as "Provider". */}
            {!isLogin && (
              <div className="mt-6 grid grid-cols-3 gap-2 p-1 bg-cn-bone-200 rounded-2xl" data-testid="role-toggle">
                {[
                  { value: 'discharge_planner', label: 'Discharge Planner', icon: Hospital,     blurb: 'Hospital · SNF · rehab', match: ['discharge_planner'] },
                  { value: 'clinician',         label: 'Provider',          icon: Stethoscope,  blurb: 'Clinicians · home health · hospice', match: ['clinician', 'agency'] },
                  { value: 'patient',           label: 'Patient / Family',  icon: Heart,        blurb: 'Follow your care after discharge', match: ['patient'] },
                ].map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => update('role', r.match.includes(formData.role) ? formData.role : r.value)}
                    data-testid={`role-${r.value === 'clinician' ? 'provider' : r.value}`}
                    title={r.blurb}
                    className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      r.match.includes(formData.role)
                        ? 'bg-white shadow-cn-subtle text-cn-text'
                        : 'text-cn-text-muted hover:text-cn-text'
                    }`}
                  >
                    <r.icon className="w-4 h-4" /> {r.label}
                  </button>
                ))}
              </div>
            )}

            {/* Merged Provider role: one tile covers independent clinicians AND
                organizations. The kind toggle only sets the backend role value
                (clinician vs agency) — the experience is one Provider home. */}
            {!isLogin && ['clinician', 'agency'].includes(formData.role) && (
              <div className="mt-3 flex gap-2" data-testid="provider-kind-toggle">
                {[
                  { role: 'clinician', label: 'Independent clinician', tid: 'provider-kind-individual' },
                  { role: 'agency', label: 'Care organization', tid: 'provider-kind-organization' },
                ].map((k) => (
                  <button
                    key={k.role}
                    type="button"
                    onClick={() => update('role', k.role)}
                    data-testid={k.tid}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      formData.role === k.role
                        ? 'bg-cn-primary text-white border-cn-primary'
                        : 'bg-white text-cn-text-muted border-cn-border hover:text-cn-text'
                    }`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            )}

            {/* Per-role one-liner so the user knows exactly what they're signing up for. */}
            {!isLogin && (
              <p
                className="mt-3 text-xs text-cn-text-muted leading-relaxed"
                data-testid="role-helper"
              >
                {({
                  discharge_planner: "You'll create referrals, clear coverage and placement exceptions, and track every discharge to a committed first visit.",
                  agency:            "You'll accept referrals, sign orders in-platform, and commit first visits — whether you're an independent clinician or run an organization, it's one Provider home.",
                  clinician:         "You'll accept referrals, sign orders in-platform, and commit first visits — whether you're an independent clinician or run an organization, it's one Provider home.",
                  patient:           "You'll see your placement status and care team, and choose which family members can follow along. Family members see your status once you grant them access.",
                })[formData.role] || ''}
              </p>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="auth-form">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first_name" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">First name</Label>
                    <div className="relative mt-1.5">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
                      <Input
                        id="first_name" required
                        value={formData.first_name}
                        onChange={(e) => update('first_name', e.target.value)}
                        className="pl-9 h-11 rounded-xl border-cn-border bg-white"
                        placeholder="Maya"
                        data-testid="input-first-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Last name</Label>
                    <div className="relative mt-1.5">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
                      <Input
                        id="last_name" required
                        value={formData.last_name}
                        onChange={(e) => update('last_name', e.target.value)}
                        className="pl-9 h-11 rounded-xl border-cn-border bg-white"
                        placeholder="Patel"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
                  <Input
                    id="email" type="email" required
                    value={formData.email}
                    onChange={(e) => update('email', e.target.value)}
                    className="pl-9 h-11 rounded-xl border-cn-border bg-white"
                    placeholder="you@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="phone" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Phone</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
                    <Input
                      id="phone" type="tel"
                      value={formData.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      className="pl-9 h-11 rounded-xl border-cn-border bg-white"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>
                  <p className="mt-1.5 text-[11px] text-cn-text-muted">Optional — we&apos;ll text visit reminders &amp; one-tap join links.</p>
                </div>
              )}

              {isClinicianSignup && (
                <div className="rounded-2xl border border-cn-border bg-cn-bone-100 dark:bg-cn-ink-800/60 p-4 space-y-4" data-testid="clinician-onboarding-fields">
                  <div className="flex items-center gap-2 text-xs font-semibold text-cn-primary uppercase tracking-wide">
                    <ClipboardList className="w-3.5 h-3.5" /> Your professional credentials
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">License type</Label>
                      <div className="mt-1.5">
                        <Select value={formData.license_type} onValueChange={(v) => update('license_type', v)}>
                          <SelectTrigger className="h-11 rounded-xl border-cn-border bg-white dark:bg-cn-ink-800" data-testid="select-license-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LICENSE_TYPES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="license_state" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">State of licensure</Label>
                      <Input
                        id="license_state"
                        value={formData.license_state}
                        onChange={(e) => update('license_state', e.target.value)}
                        className="mt-1.5 h-11 rounded-xl border-cn-border bg-white dark:bg-cn-ink-800"
                        placeholder="e.g. TX"
                        data-testid="input-license-state"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="npi" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">NPI number <span className="normal-case font-normal text-cn-text-muted/70">(optional)</span></Label>
                    <div className="relative mt-1.5">
                      <BadgeCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
                      <Input
                        id="npi"
                        inputMode="numeric"
                        maxLength={10}
                        value={formData.npi}
                        onChange={(e) => update('npi', e.target.value.replace(/\D/g, ''))}
                        className="pl-9 h-11 rounded-xl border-cn-border bg-white dark:bg-cn-ink-800 font-mono tracking-wide"
                        placeholder="10-digit National Provider Identifier"
                        data-testid="input-npi"
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-cn-text-muted">Speeds up verification. You can add it later from your dashboard.</p>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Clinical focus areas</Label>
                    <p className="mt-0.5 text-[11px] text-cn-text-muted">Select all that apply — this powers your AI matches & specialty badges.</p>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="clinical-focus-picker">
                      {CLINICAL_FOCUS_TRACKS.map((t) => {
                        const active = formData.clinical_focus_areas.includes(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => toggleFocus(t.id)}
                            data-testid={`focus-chip-${t.id}`}
                            aria-pressed={active}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              active
                                ? 'bg-cn-primary text-white border-cn-primary'
                                : 'bg-white dark:bg-cn-ink-800 text-cn-text border-cn-border hover:border-cn-primary/40'
                            }`}
                          >
                            <t.Icon className="w-3.5 h-3.5" /> {t.name}
                            {t.featured && (
                              <span className={`text-[9px] uppercase font-bold tracking-wide ${active ? 'text-cn-terracotta-200' : 'text-cn-terracotta-600'}`}>· High demand</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="password" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Password</Label>
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={(e) => update('password', e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  testid="input-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="cn-btn cn-btn-primary w-full h-12 text-base disabled:opacity-60"
                data-testid="auth-submit-button"
              >
                {loading
                  ? <span className="inline-flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Please wait…</span>
                  : <>{isLogin ? 'Sign in' : 'Create account'} <ArrowRight className="w-4 h-4" /></>
                }
              </button>

              {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <div className="relative my-3" data-testid="auth-google-divider">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-cn-border" /></div>
                <div className="relative flex justify-center"><span className="bg-white dark:bg-cn-ink-900 px-3 text-xs uppercase tracking-wider text-cn-text-muted font-semibold">or</span></div>
              </div>
              <button
                type="button"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + '/patient/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-12 rounded-2xl border border-cn-border bg-white dark:bg-cn-ink-800 hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 font-semibold text-cn-text dark:text-white inline-flex items-center justify-center gap-3 transition-colors"
                data-testid="auth-google-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              {isLogin && (
                <div className="space-y-2.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setShowMagic(true)}
                    className="font-semibold text-cn-primary hover:underline"
                    data-testid="auth-magic-link-btn"
                  >
                    Send me a one-tap sign-in link
                  </button>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="font-semibold text-cn-primary hover:underline"
                      data-testid="auth-forgot-password-btn"
                    >
                      Forgot password?
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSignInHelp(true)}
                      className="font-semibold text-cn-text-muted hover:text-cn-primary"
                      data-testid="auth-signin-help-btn"
                    >
                      Need help signing in?
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <TrustBadge variant="encrypted" />
                <TrustBadge variant="verified" label="HIPAA-ready" />
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-cn-text-muted">
              {isLogin ? "Don't have an account?" : 'Already a member?'}{' '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-cn-primary font-semibold hover:underline"
                data-testid="auth-toggle-mode"
              >
                {isLogin ? 'Create one' : 'Sign in'}
              </button>
            </div>

            <div className="mt-8">
              <MarketplaceDisclaimer variant="micro" />
            </div>
          </div>
        </div>
      </div>

      {showMagic && (
        <MagicLinkModal
          email={formData.email}
          onClose={() => setShowMagic(false)}
          onSuccess={(role) => { setShowMagic(false); navigateByRole(role); }}
          onMfaRequired={(mfaToken, email) => { setShowMagic(false); setMfaChallenge({ mfaToken, email }); }}
        />
      )}
      {mfaChallenge && (
        <MfaChallengeModal
          mfaToken={mfaChallenge.mfaToken}
          email={mfaChallenge.email}
          onClose={() => setMfaChallenge(null)}
          onSuccess={(role) => { setMfaChallenge(null); navigateByRole(role); }}
        />
      )}
      {showForgot && (
        <ForgotPasswordModal
          email={formData.email}
          onClose={() => setShowForgot(false)}
        />
      )}
      {showSignInHelp && (
        <SignInHelpModal onClose={() => setShowSignInHelp(false)} />
      )}
    </div>
  );
}

/* ============== Magic Link Modal ============== */
function MagicLinkModal({ email: initialEmail, onClose, onSuccess, onMfaRequired }) {
  const { backdropProps, cardProps } = useModalClose({ open: true, onClose });
  const { setAuthFromToken } = useAuth();
  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState(null);
  const [loading, setLoading] = useState(false);

  const requestLink = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/magic-link`, { email });
      setDevCode(res.data.dev_code || null);
      setStep('code');
      toast.success(res.data.dev_code ? `Code sent · dev code shown for testing` : 'Check your email for the 6-digit code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the link. Try again.');
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (!code) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/magic-link/verify`, { email, code });
      if (res.data?.mfa_required) {
        onMfaRequired(res.data.mfa_token, email);
        return;
      }
      const user = setAuthFromToken(res.data.token, res.data.user);
      toast.success('Signed in. Welcome back.');
      onSuccess(user.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify that code.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up" onClick={onClose} data-testid="magic-link-modal">
      <div className="relative w-full max-w-md rounded-3xl bg-white border border-cn-border shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Passwordless</p>
                <h3 className="font-display font-extrabold text-xl">One-tap sign-in</h3>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation" style={CLOSE_BTN_STYLE} data-testid="magic-link-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {step === 'email' ? (
            <form onSubmit={requestLink} className="space-y-3">
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Email on your account</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl" data-testid="magic-link-email" autoFocus />
              <p className="text-xs text-cn-text-muted">We&apos;ll send a 6-digit code — no password needed.</p>
              <button type="submit" disabled={loading || !email} className="cn-btn cn-btn-primary w-full h-12 disabled:opacity-60" data-testid="magic-link-send">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send my code <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-3">
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Enter the 6-digit code</Label>
              <Input type="text" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" className="h-12 rounded-xl text-center text-2xl tracking-[0.5em] font-mono" data-testid="magic-link-code" autoFocus />
              {devCode && process.env.NODE_ENV !== 'production' && <p className="text-[11px] text-cn-text-muted text-center">Dev mode code: <span className="font-mono font-bold text-cn-primary">{devCode}</span></p>}
              <button type="submit" disabled={loading || code.length !== 6} className="cn-btn cn-btn-primary w-full h-12 disabled:opacity-60" data-testid="magic-link-verify">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button type="button" onClick={() => setStep('email')} className="text-xs text-cn-text-muted hover:text-cn-primary w-full">Use a different email</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============== MFA Challenge Modal ============== */
function MfaChallengeModal({ mfaToken, email, onClose, onSuccess }) {
  const { backdropProps, cardProps } = useModalClose({ open: true, onClose });
  const { verifyMfaLogin } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const user = await verifyMfaLogin(mfaToken, code.trim());
      toast.success('Welcome back to CaraLoom');
      onSuccess(user.role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify that code.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up" {...backdropProps} data-testid="mfa-challenge-modal">
      <div className="relative w-full max-w-md rounded-3xl bg-white border border-cn-border shadow-2xl overflow-hidden" {...cardProps}>
        <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Two-factor</p>
                <h3 className="font-display font-extrabold text-xl">Verify it&rsquo;s you</h3>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation" style={CLOSE_BTN_STYLE} data-testid="mfa-challenge-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <form onSubmit={submit} className="space-y-3">
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Authenticator code</Label>
            <Input type="text" inputMode="numeric" maxLength={9} required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="h-12 rounded-xl text-center text-2xl tracking-[0.4em] font-mono" data-testid="mfa-challenge-code" autoFocus />
            <p className="text-xs text-cn-text-muted">
              Enter the 6-digit code from your authenticator app{email ? <> for <span className="font-medium text-cn-text">{email}</span></> : null}. You can also use a backup code.
            </p>
            <button type="submit" disabled={loading || !code.trim()} className="cn-btn cn-btn-primary w-full h-12 disabled:opacity-60" data-testid="mfa-challenge-verify">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Verify &amp; sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={onClose} className="text-xs text-cn-text-muted hover:text-cn-primary w-full" data-testid="mfa-challenge-cancel">Use a different account</button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ============== Forgot Password Modal ============== */
function ForgotPasswordModal({ email: initialEmail, onClose }) {
  const { backdropProps, cardProps } = useModalClose({ open: true, onClose });
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email | sent
  const [email, setEmail] = useState(initialEmail || '');
  const [devToken, setDevToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setDevToken(res.data.dev_reset_token || null);
      setStep('sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the reset. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up" {...backdropProps} data-testid="forgot-password-modal">
      <div className="relative w-full max-w-md rounded-3xl bg-white border border-cn-border shadow-2xl overflow-hidden" {...cardProps}>
        <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Account recovery</p>
                <h3 className="font-display font-extrabold text-xl">Reset your password</h3>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation" style={CLOSE_BTN_STYLE} data-testid="forgot-password-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {step === 'email' ? (
            <form onSubmit={requestReset} className="space-y-3">
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Email on your account</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl" data-testid="forgot-password-email" autoFocus />
              <p className="text-xs text-cn-text-muted">We&apos;ll send a secure link to reset your password. The link is single-use and expires in 1 hour.</p>
              <button type="submit" disabled={loading || !email} className="cn-btn cn-btn-primary w-full h-12 disabled:opacity-60" data-testid="forgot-password-send">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          ) : (
            <div className="space-y-4 text-center" data-testid="forgot-password-sent">
              <div className="w-14 h-14 rounded-full bg-cn-soft flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-cn-primary" />
              </div>
              <div>
                <h4 className="font-display text-lg font-bold text-cn-text">Check your email</h4>
                <p className="text-sm text-cn-text-muted mt-1">
                  If an account exists for <span className="font-medium text-cn-text">{email}</span>, a reset link is on its way.
                </p>
              </div>
              {devToken && (
                <div className="rounded-2xl bg-cn-soft/50 border border-cn-border p-4 text-left">
                  <p className="text-[11px] uppercase tracking-wide font-bold text-cn-text-muted mb-2">Preview mode — reset link also shown here</p>
                  <button
                    onClick={() => { onClose(); navigate(`/reset-password?token=${encodeURIComponent(devToken)}`); }}
                    className="cn-btn cn-btn-primary w-full h-11"
                    data-testid="forgot-password-dev-continue"
                  >
                    Continue to reset <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button onClick={onClose} className="text-xs text-cn-text-muted hover:text-cn-primary w-full" data-testid="forgot-password-done">Back to sign in</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============== Sign-in Help Modal (Cara-powered) ============== */
function SignInHelpModal({ onClose }) {
  const { backdropProps, cardProps } = useModalClose({ open: true, onClose });
  const [messages, setMessages] = useState([{ role: 'assistant', content: "Hi — I'm Cara. Tell me what's happening and I'll get you back in. (Tip: I can send you a one-tap sign-in link.)" }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message) return;
    const next = [...messages, { role: 'user', content: message }];
    setMessages(next); setInput(''); setBusy(true);
    try {
      const res = await axios.post(`${API}/lynk/login-help`, { message, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages([...next, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages([...next, { role: 'assistant', content: "Sorry — I'm having trouble reaching the system. If you can, try the 'Send me a one-tap sign-in link' button instead." }]);
    } finally { setBusy(false); }
  };

  const QUICK_PROMPTS = [
    "I forgot my password",
    "I can't find my email",
    "It says my account is locked",
    "I got a new phone — how do I sign in?",
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in-up" {...backdropProps} data-testid="signin-help-modal">
      <div className="relative w-full max-w-md rounded-3xl bg-white border border-cn-border shadow-2xl overflow-hidden flex flex-col max-h-[80vh]" {...cardProps}>
        <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #0F172A, #334155, #334155)' }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Sign-in help</p>
                <h3 className="font-display font-extrabold text-xl">Cara · here for you</h3>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors touch-manipulation" style={CLOSE_BTN_STYLE} data-testid="signin-help-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-cn-bg/40">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm ${
                m.role === 'user' ? 'bg-cn-primary text-white rounded-br-md' : 'bg-white border border-cn-border/60 text-cn-text rounded-bl-md'
              }`}>
                {m.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown components={HELP_MD_COMPONENTS}>{m.content}</ReactMarkdown>
                  </div>
                ) : <p className="whitespace-pre-wrap">{m.content}</p>}
              </div>
            </div>
          ))}
          {messages.length === 1 && (
            <div className="grid grid-cols-1 gap-2 pt-1">
              {QUICK_PROMPTS.map((q, i) => (
                <button key={i} onClick={() => send(q)} data-testid={`signin-quick-${i}`} className="text-left text-sm px-3 py-2.5 rounded-2xl bg-white border border-cn-border/60 hover:border-cn-primary/40 text-cn-text">
                  <Wand2 className="w-3.5 h-3.5 text-cn-primary inline mr-2" />{q}
                </button>
              ))}
            </div>
          )}
          {busy && <div className="flex items-center gap-2 text-sm text-cn-text-muted px-2"><Loader2 className="w-4 h-4 animate-spin text-cn-primary" /> Cara is thinking…</div>}
          <div ref={bottomRef} />
        </div>
        <div className="p-3 border-t border-cn-border/60 bg-white">
          <div className="flex items-center gap-2 bg-cn-bg border border-cn-border/60 rounded-2xl p-1.5">
            <MessageSquare className="w-4 h-4 ml-2 text-cn-text-muted" />
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Tell Cara what's happening" data-testid="signin-help-input" className="flex-1 bg-transparent outline-none text-sm" disabled={busy} />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="signin-help-send" className="p-2 rounded-xl text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}><Send className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
